import React, { useEffect, useMemo, useState } from 'react';
import { formatDateRange } from '@/lib/utils';
import { EventDates, EventExceptions } from '@/types/EventInterface';
import { getEventDates } from '@/lib/events';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Skeleton } from './ui/skeleton';

interface Props {
  type: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  className?: string;
  recommendedAges?: string;
  eventDates: EventDates[];
  exceptions?: EventExceptions[];
}

interface RenderIconProps {
  src: string;
  alt: string;
}

const RenderIcon: React.FC<RenderIconProps> = ({ src, alt }) => (
  <img
    src={src}
    className='mr-1'
    alt={alt}
    loading='lazy'
  />
);

interface RenderLoadingSkeletonProps {
  width: string;
}

const RenderLoadingSkeleton: React.FC<RenderLoadingSkeletonProps> = ({ width }) => (
  <div className='flex items-center'>
    <RenderIcon
      src='/images/calender-icon.svg'
      alt='Calendar Icon'
    />
    <Skeleton className={`h-5 ${width}`} />
  </div>
);

const TimeLocation: React.FC<Props> = ({
  type,
  startDate,
  endDate,
  location,
  className,
  recommendedAges,
  eventDates,
  exceptions = [],
}) => {
  const [selectedEventTime, setSelectedEventTime] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(type === 'event');
  const timeZone = 'America/New_York';

  // Process all event dates using the getEventDates utility
  const allDates = useMemo(
    () => getEventDates([{ eventDates, exceptions }]),
    [eventDates, exceptions],
  );

  // Create a Set of cancelled event dates for quick lookup
  const cancelledDatesSet = useMemo(() => {
    return new Set(exceptions.filter((ex) => ex.cancelEvent).map((d) => d.exceptionDate));
  }, [exceptions]);

  // Filter out cancelled dates and get the first available one
  const nextAvailableDate = useMemo(() => {
    return allDates.find((event) => !cancelledDatesSet.has(format(event.date, 'yyyy-MM-dd')));
  }, [allDates, cancelledDatesSet]);

  useEffect(() => {
    if (!nextAvailableDate) {
      setSelectedEventTime(null);
      setSelectedDate(null);
      setIsLoading(false);
      return;
    }

    const dateString = format(nextAvailableDate.date, 'yyyy-MM-dd');
    const exception = exceptions.find((ex) => ex.exceptionDate === dateString);

    const formattedDate = formatInTimeZone(nextAvailableDate.date, timeZone, 'MMMM d, yyyy');

    const getFormattedTime = (dateStr: string, timeStr: string) => {
      return formatInTimeZone(new Date(`${dateStr}T${timeStr}`), timeZone, 'h:mm a');
    };

    let startTime: string;
    let endTime: string;

    if (exception?.timeDuration) {
      // Use modified time from exception
      startTime = getFormattedTime(dateString, exception.timeDuration.startTime);
      endTime = getFormattedTime(dateString, exception.timeDuration.endTime);
    } else {
      // Use default event time
      startTime = formatInTimeZone(nextAvailableDate.startTime, timeZone, 'h:mm a');
      endTime = formatInTimeZone(nextAvailableDate.endTime, timeZone, 'h:mm a');
    }

    setSelectedDate(formattedDate);
    setSelectedEventTime(`${startTime} - ${endTime} EST`);
    setIsLoading(false);
  }, [nextAvailableDate, exceptions]);

  return (
    <div className={className}>
      <div className='flex flex-wrap justify-between'>
        {/* Date */}
        {isLoading ? (
          <RenderLoadingSkeleton width='w-24' />
        ) : selectedDate ? (
          <div className='flex items-center lowercase'>
            <RenderIcon
              src='/images/calender-icon.svg'
              alt='Calendar Icon'
            />
            <span>{selectedDate}</span>
          </div>
        ) : null}

        {startDate && (
          <div className='flex items-center lowercase'>
            <RenderIcon
              src='/images/calender-icon.svg'
              alt='Calendar Icon'
            />
            <span>{formatDateRange(startDate, endDate)}</span>
          </div>
        )}

        {/* Time */}
        {isLoading && type === 'event' ? (
          <RenderLoadingSkeleton width='w-36' />
        ) : selectedEventTime ? (
          <div className='flex items-center lowercase'>
            <RenderIcon
              src='/images/time-icon.svg'
              alt='Time Icon'
            />
            <span>{selectedEventTime}</span>
          </div>
        ) : null}

        {/* Location */}
        {location && (
          <div className='flex items-center'>
            <RenderIcon
              src='/images/location-icon.svg'
              alt='Location Icon'
            />
            <span>{location}</span>
          </div>
        )}

        {/* Recommended Ages */}
        {recommendedAges && (
          <div className='flex items-center'>
            <RenderIcon
              src='/images/ages-icon.svg'
              alt='Ages Icon'
            />
            <span>recommended ages: {recommendedAges}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeLocation;
